import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useCampaigns, useCreateBudget, usePromotions, useUpdateCampaign } from '../../hooks';
import type { ToastSeverity } from '../../lib/useToast';
import type { BudgetCapKind, BudgetOnExhaust, BudgetPeriod, BudgetResponse, BudgetScope } from '../../model/types';

export interface BudgetFormDrawerProps {
  open: boolean;
  onClose: () => void;
  onCreated: (budget: BudgetResponse) => void;
  onResult: (severity: ToastSeverity, message: string) => void;
}

interface Draft {
  scope: BudgetScope;
  campaignId: string;
  promotionId: string;
  capKind: BudgetCapKind;
  period: BudgetPeriod;
  capValue: string;
  alertThresholdPct: string;
  onExhaust: BudgetOnExhaust;
}

const EMPTY: Draft = {
  scope: 'CAMPAIGN',
  campaignId: '',
  promotionId: '',
  capKind: 'MONEY',
  period: 'TOTAL',
  capValue: '',
  alertThresholdPct: '80',
  onExhaust: 'AUTO_PAUSE',
};

/**
 * "New budget" — create a spend/usage budget (plan-011 §8.6).
 *
 * Contract-driven scope reduction: the shipped BFF exposes only `POST /budgets`, `GET
 * /budgets/{id}` and `GET /budgets/{id}/burn` — there is no update or delete endpoint, so this
 * drawer is create-only (the spec's "edit"/"delete inside the drawer" isn't backed by the real
 * contract). CAMPAIGN scope is completed by a follow-up `PUT` on the campaign (setting its
 * `budgetId`) since that's the only way the BFF associates a campaign with its budget.
 */
export function BudgetFormDrawer({ open, onClose, onCreated, onResult }: BudgetFormDrawerProps) {
  const { t } = useTranslation('promotions');
  const [draft, setDraft] = useState<Draft>(EMPTY);

  const campaigns = useCampaigns({ size: 200 }, { enabled: open });
  const promotions = usePromotions({ size: 200 }, { enabled: open });
  const createBudget = useCreateBudget();
  const updateCampaign = useUpdateCampaign();
  const pending = createBudget.isPending || updateCampaign.isPending;

  const isValid =
    Number(draft.capValue) > 0 &&
    (draft.scope === 'CAMPAIGN' ? draft.campaignId.length > 0 : draft.promotionId.length > 0);

  const handleCreate = async () => {
    try {
      const created = await createBudget.mutateAsync({
        scope: draft.scope,
        promotionId: draft.scope === 'PROMOTION' ? draft.promotionId : undefined,
        capKind: draft.capKind,
        period: draft.period,
        capValue: Number(draft.capValue),
        alertThresholdPct: draft.alertThresholdPct.trim() ? Number(draft.alertThresholdPct) : undefined,
        onExhaust: draft.onExhaust,
      });

      if (draft.scope === 'CAMPAIGN') {
        const campaign = campaigns.data?.content.find((c) => c.id === draft.campaignId);
        if (campaign) {
          await updateCampaign.mutateAsync({
            campaignId: campaign.id,
            body: {
              code: campaign.code,
              name: campaign.name,
              description: campaign.description ?? undefined,
              startsAt: campaign.startsAt,
              endsAt: campaign.endsAt,
              budgetId: created.id,
            },
          });
        }
      }

      onResult('success', t('budgetFormDrawer.created'));
      setDraft(EMPTY);
      onCreated(created);
      onClose();
    } catch (err) {
      onResult('error', (err as { message?: string }).message ?? t('budgetFormDrawer.createError'));
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: '100%', sm: 440 }, display: 'flex', flexDirection: 'column' } } }}
    >
      <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t('budgetFormDrawer.title')}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2.5 }}>
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t('budgetFormDrawer.scopeLabel')}
            </Typography>
            <RadioGroup
              row
              value={draft.scope}
              onChange={(e) => setDraft((d) => ({ ...d, scope: e.target.value as BudgetScope }))}
            >
              <FormControlLabel value="CAMPAIGN" control={<Radio />} label={t('budgetFormDrawer.scope.CAMPAIGN')} />
              <FormControlLabel value="PROMOTION" control={<Radio />} label={t('budgetFormDrawer.scope.PROMOTION')} />
            </RadioGroup>
          </Box>

          {draft.scope === 'CAMPAIGN' ? (
            <TextField
              select
              label={t('budgetFormDrawer.campaignLabel')}
              required
              value={draft.campaignId}
              onChange={(e) => setDraft((d) => ({ ...d, campaignId: e.target.value }))}
              fullWidth
            >
              {(campaigns.data?.content ?? []).map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <TextField
              select
              label={t('budgetFormDrawer.promotionLabel')}
              required
              value={draft.promotionId}
              onChange={(e) => setDraft((d) => ({ ...d, promotionId: e.target.value }))}
              fullWidth
            >
              {(promotions.data?.content ?? []).map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </TextField>
          )}

          <TextField
            select
            label={t('budgetFormDrawer.capKindLabel')}
            value={draft.capKind}
            onChange={(e) => setDraft((d) => ({ ...d, capKind: e.target.value as BudgetCapKind }))}
            fullWidth
          >
            <MenuItem value="MONEY">{t('budgetFormDrawer.capKind.MONEY')}</MenuItem>
            <MenuItem value="REDEMPTIONS">{t('budgetFormDrawer.capKind.REDEMPTIONS')}</MenuItem>
          </TextField>

          <TextField
            label={t('budgetFormDrawer.capValueLabel')}
            type="number"
            required
            value={draft.capValue}
            onChange={(e) => setDraft((d) => ({ ...d, capValue: e.target.value }))}
            slotProps={{ htmlInput: { min: 1 } }}
            fullWidth
          />

          <TextField
            select
            label={t('budgetFormDrawer.periodLabel')}
            value={draft.period}
            onChange={(e) => setDraft((d) => ({ ...d, period: e.target.value as BudgetPeriod }))}
            fullWidth
          >
            <MenuItem value="TOTAL">{t('budgetFormDrawer.period.TOTAL')}</MenuItem>
            <MenuItem value="DAILY">{t('budgetFormDrawer.period.DAILY')}</MenuItem>
          </TextField>

          <TextField
            label={t('budgetFormDrawer.thresholdLabel')}
            type="number"
            value={draft.alertThresholdPct}
            onChange={(e) => setDraft((d) => ({ ...d, alertThresholdPct: e.target.value }))}
            slotProps={{ htmlInput: { min: 1, max: 100 } }}
            fullWidth
          />

          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t('budgetFormDrawer.onExhaustLabel')}
            </Typography>
            <RadioGroup
              value={draft.onExhaust}
              onChange={(e) => setDraft((d) => ({ ...d, onExhaust: e.target.value as BudgetOnExhaust }))}
            >
              <FormControlLabel
                value="AUTO_PAUSE"
                control={<Radio />}
                label={t('budgetFormDrawer.onExhaust.AUTO_PAUSE')}
              />
              <FormControlLabel
                value="NOTIFY_ONLY"
                control={<Radio />}
                label={t('budgetFormDrawer.onExhaust.NOTIFY_ONLY')}
              />
            </RadioGroup>
          </Box>
        </Stack>
      </Box>

      <Box sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
        <Button onClick={onClose} disabled={pending}>
          {t('common:action.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleCreate()}
          disabled={!isValid || pending}
          startIcon={pending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {t('common:action.create')}
        </Button>
      </Box>
    </Drawer>
  );
}
